import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { reportingService } from '../../../services/reportingService';

const WorkOrderDetailModal = ({ workOrderId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (workOrderId) {
      loadWorkOrderDetails();
    }
  }, [workOrderId]);

  const loadWorkOrderDetails = async () => {
    setLoading(true);
    try {
      const data = await reportingService?.getWorkOrderFinancialDetails(workOrderId);
      setDetails(data);
    } catch (error) {
      console.error('Error loading work order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `$${value?.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr)?.toLocaleDateString('es-MX', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-3 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success/10 rounded-lg">
              <Icon name="FileText" size={24} className="text-success" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-bold text-foreground">
                Orden de Trabajo #{details?.folioOt || '...'}
              </h2>
              <p className="text-sm text-muted-foreground">
                Desglose Financiero Detallado
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Icon name="Loader2" className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Work Order Info */}
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Cliente</p>
                    <p className="font-semibold text-foreground flex items-center gap-2">
                      <Icon name="User" size={16} />
                      {details?.clientName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Vehículo</p>
                    <p className="font-semibold text-foreground flex items-center gap-2">
                      <Icon name="Car" size={16} />
                      {details?.vehicleInfo}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Técnico Asignado</p>
                    <p className="font-semibold text-foreground flex items-center gap-2">
                      <Icon name="Wrench" size={16} />
                      {details?.technicianName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Fecha de Ingreso</p>
                    <p className="font-semibold text-foreground flex items-center gap-2">
                      <Icon name="Calendar" size={16} />
                      {formatDate(details?.fechaIngreso)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-primary/5 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="ShoppingCart" size={18} className="text-primary" />
                    <span className="text-sm text-muted-foreground">Subtotal</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(details?.subtotal || 0)}
                  </p>
                </div>
                <div className="bg-warning/5 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Percent" size={18} className="text-warning" />
                    <span className="text-sm text-muted-foreground">IVA (16%)</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(details?.iva || 0)}
                  </p>
                </div>
                <div className="bg-success/5 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="DollarSign" size={18} className="text-success" />
                    <span className="text-sm text-muted-foreground">Total</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(details?.total || 0)}
                  </p>
                </div>
              </div>

              {/* Items Breakdown */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="List" size={20} />
                  Desglose de Servicios y Refacciones
                </h3>
                <div className="space-y-2">
                  {details?.items?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No hay items registrados
                    </p>
                  ) : (
                    <>
                      {/* Table Header */}
                      <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 bg-muted/50 rounded-lg text-sm font-semibold text-muted-foreground">
                        <div className="col-span-5">Descripción</div>
                        <div className="col-span-2 text-center">Tipo</div>
                        <div className="col-span-1 text-center">Cant.</div>
                        <div className="col-span-2 text-right">Precio Unit.</div>
                        <div className="col-span-2 text-right">Subtotal</div>
                      </div>

                      {/* Table Rows */}
                      {details?.items?.map((item, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 py-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="col-span-1 md:col-span-5">
                            <p className="font-medium text-foreground">{item?.descripcion}</p>
                            {item?.categoria && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {item?.categoria}
                              </p>
                            )}
                          </div>
                          <div className="col-span-1 md:col-span-2 md:text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              item?.tipo === 'servicio' ?'bg-primary/10 text-primary' :'bg-warning/10 text-warning'
                            }`}>
                              <Icon 
                                name={item?.tipo === 'servicio' ? 'Wrench' : 'Package'} 
                                size={12} 
                              />
                              {item?.tipo === 'servicio' ? 'Servicio' : 'Refacción'}
                            </span>
                          </div>
                          <div className="col-span-1 md:col-span-1 md:text-center">
                            <p className="text-foreground">{item?.cantidad}</p>
                          </div>
                          <div className="col-span-1 md:col-span-2 md:text-right">
                            <p className="text-foreground">{formatCurrency(item?.precioUnitario)}</p>
                          </div>
                          <div className="col-span-1 md:col-span-2 md:text-right">
                            <p className="font-semibold text-success">
                              {formatCurrency(item?.subtotal)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Payment Status */}
              {details?.invoiceStatus && (
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="Receipt" size={20} className="text-primary" />
                      <span className="font-semibold text-foreground">Estado de Factura</span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      details?.invoiceStatus === 'pagado' 
                        ? 'bg-success/10 text-success' 
                        : details?.invoiceStatus === 'pendiente' ?'bg-warning/10 text-warning' :'bg-destructive/10 text-destructive'
                    }`}>
                      {details?.invoiceStatus === 'pagado' ? 'Pagado' : 
                       details?.invoiceStatus === 'pendiente' ? 'Pendiente' : 'Vencido'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOrderDetailModal;